/**
 * The flag-by-flag step-through. This is the one place the design deliberately
 * costs a click, and only on counts that tripped a rule: a flagged count has
 * no Approve button anywhere, so this is the only route to applying it.
 *
 * Four ways out of a flag:
 *   Reject this line       leave it as the ERP had it, and close it
 *   Send back for recount  leave it unchanged, name it in the summary
 *   Ask {org}              a pre-written, editable question; the count moves
 *                          to "Waiting on 3PL" and nothing is applied
 *   Looks right, keep it   accept it as counted
 *
 * The "not counted" card is an acknowledgement, not a decision: blank lines
 * are never written, so there is nothing to reject or recount. It offers the
 * question and the confirmation only.
 */
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeftRight, ArrowRight, Flag, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { StocktakeProposalLineRow } from '@threadheads/shared';
import { dropPercent, type FlagCard } from '../lib/flag-cards';
import { formatQty, formatSigned } from '../lib/format';

export type TriageResolution = 'keep' | 'recount' | 'reject';

interface FlagTriageProps {
  cards: FlagCard[];
  stepIndex: number;
  locationName: string;
  counterpartyName: string;
  uncountedCount: number;
  acceptedLineCount: number;
  /** Position in a multi-count bulk queue, 1-based. Null when single. */
  queuePosition: { index: number; total: number } | null;
  onCancel: () => void;
  onResolve: (card: FlagCard, resolution: TriageResolution) => void;
  onAsk: (card: FlagCard) => void;
  onApprove: () => void;
  isPending: boolean;
  /** Rendered in place of the action row while a question is being written. */
  composer?: React.ReactNode;
}

function PairBox({
  line,
  className,
}: {
  line: StocktakeProposalLineRow;
  className?: string;
}) {
  const { t } = useTranslation('inventory');
  const delta = line.countedQty == null ? null : line.countedQty - line.expectedQty;
  return (
    <div className={cn('rounded-md border bg-muted/50 px-3 py-2.5', className)}>
      <div className="font-mono text-[11.5px] font-medium">{line.sku ?? '-'}</div>
      {/* "expected -> counted" reads as a glyph, so it needs no translation
          and the counted figure keeps its own semantic colour. */}
      <div className="mt-1 flex items-center gap-1.5 text-base font-semibold tabular-nums">
        <span className="text-muted-foreground">{formatQty(line.expectedQty)}</span>
        <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
        <span
          className={cn(
            delta == null || delta === 0
              ? 'text-muted-foreground'
              : delta > 0
                ? 'text-emerald-700'
                : 'text-destructive',
          )}
        >
          {line.countedQty == null
            ? t('stocktakes.triage.blank')
            : formatQty(line.countedQty)}
        </span>
      </div>
      {delta != null ? (
        <div className="mt-0.5 text-[11px] tabular-nums text-muted-foreground">
          {formatSigned(delta)}
        </div>
      ) : null}
    </div>
  );
}

export function FlagTriage({
  cards,
  stepIndex,
  locationName,
  counterpartyName,
  uncountedCount,
  acceptedLineCount,
  queuePosition,
  onCancel,
  onResolve,
  onAsk,
  onApprove,
  isPending,
  composer,
}: FlagTriageProps) {
  const { t } = useTranslation('inventory');
  const total = cards.length;
  const card = cards[stepIndex];
  const doneButtonRef = useRef<HTMLButtonElement>(null);

  const title = card
    ? card.kind === 'mirror'
      ? t('stocktakes.triage.mirrorTitle')
      : card.kind === 'drop'
        ? t('stocktakes.triage.dropTitle', {
            sku: card.lines[0]?.sku ?? '',
            pct: card.lines[0] ? dropPercent(card.lines[0]) : 0,
            expected: formatQty(card.lines[0]?.expectedQty ?? 0),
            counted: formatQty(card.lines[0]?.countedQty ?? 0),
          })
        : t('stocktakes.triage.uncountedTitle', { count: card.lines.length })
    : t('stocktakes.triage.doneTitle', { count: total, location: locationName });

  // Clearing the last flag swaps the whole panel out from under the button
  // that was just clicked, so focus would otherwise land on <body>. Move it to
  // the one action the done panel offers. Keyed on the BOOLEAN, not on `card`:
  // the card objects churn identity on every refetch, and re-focusing then
  // would yank focus off whatever the reviewer had tabbed to.
  const isDone = !card;
  useEffect(() => {
    if (isDone) doneButtonRef.current?.focus();
  }, [isDone]);

  // --- every flag cleared -------------------------------------------------
  if (!card) {
    return (
      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
        <div
          role="status"
          className="rounded-md bg-emerald-100 px-3 py-2.5 text-[12.5px] text-emerald-800 ring-1 ring-emerald-300/80"
        >
          <b className="font-semibold">{title}</b>
          <br />
          {t('stocktakes.triage.doneBody')}
          {uncountedCount > 0 ? (
            <>
              {' '}
              {t('stocktakes.triage.doneBodyUncounted', { count: uncountedCount })}
            </>
          ) : null}
        </div>
        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="outline" size="sm" className="h-8 px-3 text-xs" onClick={onCancel}>
            {t('stocktakes.triage.back')}
          </Button>
          <Button
            ref={doneButtonRef}
            size="sm"
            className="h-8 gap-1.5 bg-threadheads-purple px-3 text-xs text-white hover:bg-threadheads-purple/90"
            onClick={onApprove}
            disabled={isPending}
          >
            {isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
            ) : null}
            {t('stocktakes.triage.approveAndApply', {
              count: acceptedLineCount,
              lines: formatQty(acceptedLineCount),
            })}
          </Button>
        </div>
      </div>
    );
  }

  const description =
    card.kind === 'mirror'
      ? t('stocktakes.triage.mirrorBody')
      : card.kind === 'drop'
        ? t('stocktakes.triage.dropBody')
        : t('stocktakes.triage.uncountedBody');

  const firstSkus = card.lines
    .slice(0, 3)
    .map((line) => line.sku)
    .filter(Boolean)
    .join(', ');

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
      <div className="flex flex-wrap items-center gap-2.5 border-b pb-2.5">
        {/* Hidden from AT: the same string is spoken as part of the live
            heading below, and hearing "Flag 2 of 3" twice per step is noise. */}
        <span className="text-[11.5px] font-semibold text-muted-foreground" aria-hidden="true">
          {t('stocktakes.triage.step', { step: stepIndex + 1, total })}
        </span>
        <span
          className="h-1 min-w-[90px] flex-1 overflow-hidden rounded-full bg-muted"
          role="progressbar"
          aria-valuenow={stepIndex}
          aria-valuemin={0}
          aria-valuemax={total}
          aria-label={t('stocktakes.triage.progressAria')}
        >
          {/* scaleX, not width: animating width relayouts the bar every frame. */}
          <span
            className="block h-full w-full origin-left rounded-full bg-threadheads-purple transition-transform duration-200 ease-out motion-reduce:transition-none"
            style={{ transform: `scaleX(${total > 0 ? stepIndex / total : 0})` }}
          />
        </span>
        {queuePosition ? (
          <span className="text-[11.5px] text-muted-foreground">
            {t('stocktakes.triage.queue', {
              index: queuePosition.index,
              total: queuePosition.total,
            })}
          </span>
        ) : null}
        <Button variant="outline" size="sm" className="h-6 px-2 text-[11px]" onClick={onCancel}>
          {t('stocktakes.triage.cancel')}
        </Button>
      </div>

      <div className="mt-3.5 rounded-lg border bg-card p-4 shadow-sm">
        {/* Live: stepping to the next flag replaces this heading in place, with
            no focus move to carry the change to a screen reader. Scoped to the
            heading block so a spinner toggling elsewhere does not re-announce
            the whole card. */}
        <div className="flex items-start gap-2.5" role="status">
          <span className="mt-0.5 inline-flex shrink-0 items-center rounded-md bg-amber-100 px-1.5 py-0.5 text-amber-800 ring-1 ring-amber-300/80">
            <Flag className="h-3 w-3" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <b className="text-sm font-semibold">
              <span className="sr-only">
                {t('stocktakes.triage.step', { step: stepIndex + 1, total })}.{' '}
              </span>
              {title}
            </b>
            <p className="mt-1 max-w-[70ch] text-[12.5px] text-muted-foreground">{description}</p>
          </div>
        </div>

        {card.kind === 'mirror' ? (
          <div className="my-3 grid items-center gap-2.5 sm:grid-cols-[1fr_auto_1fr]">
            {card.lines[0] ? <PairBox line={card.lines[0]} /> : null}
            <ArrowLeftRight
              className="mx-auto h-4 w-4 text-amber-600"
              aria-hidden="true"
            />
            {card.lines[1] ? <PairBox line={card.lines[1]} /> : null}
          </div>
        ) : card.kind === 'drop' ? (
          <div className="my-3">{card.lines[0] ? <PairBox line={card.lines[0]} /> : null}</div>
        ) : (
          <div className="my-3 rounded-md border bg-muted/50 px-3 py-2.5">
            <div className="text-base font-semibold tabular-nums">
              {formatQty(card.lines.length)}{' '}
              <span className="text-xs font-normal text-muted-foreground">
                {t('stocktakes.triage.leftBlank', { count: card.lines.length })}
              </span>
            </div>
            <div className="mt-1 text-[11px] text-muted-foreground">
              {card.lines.length > 3
                ? t('stocktakes.triage.skuListMore', {
                    skus: firstSkus,
                    count: card.lines.length - 3,
                  })
                : firstSkus}
            </div>
          </div>
        )}

        {composer ?? (
          <div className="flex flex-wrap justify-end gap-2">
            {card.kind !== 'uncounted' ? (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 border-orange-300 px-3 text-xs text-orange-600 hover:bg-orange-50 hover:text-orange-700"
                  onClick={() => onResolve(card, 'reject')}
                  disabled={isPending}
                >
                  {t('stocktakes.triage.rejectLine')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 px-3 text-xs"
                  onClick={() => onResolve(card, 'recount')}
                  disabled={isPending}
                >
                  {t('stocktakes.triage.sendBack')}
                </Button>
              </>
            ) : null}
            <Button
              variant="outline"
              size="sm"
              className="h-8 border-amber-300 bg-amber-100 px-3 text-xs text-amber-800 hover:bg-amber-200 hover:text-amber-900"
              onClick={() => onAsk(card)}
              disabled={isPending}
            >
              {t('stocktakes.triage.ask', { org: counterpartyName })}
            </Button>
            <Button
              size="sm"
              className="h-8 gap-1.5 bg-threadheads-purple px-3 text-xs text-white hover:bg-threadheads-purple/90"
              onClick={() => onResolve(card, 'keep')}
              disabled={isPending}
            >
              {isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
              ) : null}
              {t('stocktakes.triage.keep')}
            </Button>
          </div>
        )}
      </div>

      <p className="mt-3 text-center text-[11.5px] text-muted-foreground">
        {t('stocktakes.triage.footnote')}
      </p>
    </div>
  );
}
